import { parseISODate } from "./format";

// VIB-1452 terminologi: BF i interne navn/DB-felter er GEEK i alt
// brukervendt. Beregningen og de kanoniske `bf_*`-kontraktene er uendret.

/**
 * Pure calculation functions for billing rates and KPIs.
 * Extracted from hooks for testability. No Supabase or React dependencies.
 */

/**
 * Intern arbeidsdag — det vi forventer at en ansatt FAKTISK jobber per dag
 * (ekskluderer betalt lunsj). Brukes til kapasitetsberegning og
 * faktureringsgrad. Skal IKKE forveksles med BILLABLE_HOURS_PER_DAY i
 * `offer-calc.ts` (8t), som er det kunden BETALER for per "dag" i tilbud.
 */
export const WORK_HOURS_PER_DAY = 7.5;

/** Year-to-date billing rate from monthly data */
export function calcYtdBillingRate(
  monthlyBillable: number[],
  monthlyCapacity: number[],
): number {
  const totalBillable = monthlyBillable.reduce((s, h) => s + h, 0);
  const totalCapacity = monthlyCapacity.reduce((s, c) => s + c, 0);
  if (totalCapacity <= 0) return 0;
  return Math.round((totalBillable / totalCapacity) * 1000) / 10;
}

/** Calculate required rate for remaining months to hit annual target */
export function calcRequiredRate(
  targetRate: number,
  monthlyCapacity: number,
  ytdBillable: number,
  completedMonths: number,
): number {
  const remainingMonths = 12 - completedMonths;
  const totalYearCapacity = monthlyCapacity * 12;
  const neededBillable = (targetRate / 100) * totalYearCapacity - ytdBillable;
  const remainingCapacity = monthlyCapacity * remainingMonths;
  if (remainingCapacity <= 0) return 0;
  return Math.round((neededBillable / remainingCapacity) * 1000) / 10;
}

/** Calculate capacity: base capacity minus absence hours */
export function calcMonthlyCapacity(
  baseCapacity: number,
  absenceHours: number,
): number {
  return Math.max(baseCapacity - absenceHours, 0);
}

/**
 * Count working days (Mon-Fri) in an overlap between two date ranges.
 * Used for calculating absence hours within a month.
 */
export function countWorkingDaysInOverlap(
  rangeStart: Date,
  rangeEnd: Date,
  periodStart: Date,
  periodEnd: Date,
): number {
  const overlapStart = rangeStart > periodStart ? rangeStart : periodStart;
  const overlapEnd = rangeEnd < periodEnd ? rangeEnd : periodEnd;

  if (overlapStart > overlapEnd) return 0;

  let days = 0;
  const d = new Date(overlapStart);
  while (d <= overlapEnd) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) days++;
    d.setDate(d.getDate() + 1);
  }
  return days;
}

/** Calculate absence hours for a user in a given month */
export function calcAbsenceHours(
  absences: Array<{
    startDate: string;
    endDate: string;
    status?: string;
    /**
     * Eksplisitt timeoverstyring. Brukes for fravær ført i deltimer (f.eks.
     * avspasering) der dato-range × 7,5t/dag ikke treffer. Når satt, tilskrives
     * hele beløpet måneden til startDate (ikke fordelt på ukedager).
     */
    hours?: number | null;
  }>,
  month: number,
  year: number,
): number {
  const monthStart = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month, 0);
  let totalHours = 0;

  for (const absence of absences) {
    if (absence.status === "rejected") continue;
    if (absence.hours != null) {
      const absStart = parseISODate(absence.startDate);
      if (absStart.getFullYear() === year && absStart.getMonth() === month - 1) {
        totalHours += absence.hours;
      }
      continue;
    }
    const absStart = parseISODate(absence.startDate);
    const absEnd = parseISODate(absence.endDate);
    const days = countWorkingDaysInOverlap(absStart, absEnd, monthStart, monthEnd);
    totalHours += days * WORK_HOURS_PER_DAY;
  }

  return totalHours;
}

/** Calculate budget deviation percentage: (actual - estimated) / estimated * 100 */
export function calcBudgetDeviation(
  bfActual: number,
  bfEstimated: number,
): number {
  if (bfEstimated <= 0) return 0;
  return Math.round(((bfActual - bfEstimated) / bfEstimated) * 1000) / 10;
}

/** Calculate weighted BF for a sales unit from project owners */
export function calcSalesUnitBf(
  projects: Array<{
    bfActual: number;
    owners: Array<{
      userId: string;
      ownershipPct: number;
      salesUnitId: string | null;
    }>;
  }>,
  salesUnitId: string,
  userId: string,
): number {
  let totalBf = 0;
  for (const p of projects) {
    for (const owner of p.owners) {
      if (owner.userId !== userId || owner.salesUnitId !== salesUnitId) continue;
      totalBf += p.bfActual * (owner.ownershipPct / 100);
    }
  }
  return totalBf;
}

/**
 * Helper functions for identifying overhead and rabatt lines
 */
export function isOverheadLine(budgetLine: { description?: string | null }): boolean {
  return /overhead/i.test(budgetLine.description ?? "");
}

export function isRabattLine(budgetLine: { description?: string | null }): boolean {
  return /rabatt/i.test(budgetLine.description ?? "");
}

export function isUpsellLine(budgetLine: { upsell_id?: string | null }): boolean {
  return budgetLine.upsell_id != null;
}

/**
 * Reise-linjer (phase = "reise") vises som egen gruppe i UI, men
 * inngår i overhead/rabatt-grunnlaget (VIB-2086).
 */
export function isTravelLine(budgetLine: { phase?: string | null }): boolean {
  return budgetLine.phase === "reise";
}

/**
 * Overhead/rabatt-rader som ikke skal vises som vanlige budsjett-oppgaver.
 * Mersalg-linjer er IKKE summary — de skal vises som oppgaver i
 * Timetildeling, men håndteres separat fra overhead-grunnlaget i
 * `calcLiveBfEstimated` via `isUpsellLine`.
 */
export function isSummaryLine(budgetLine: {
  description?: string | null;
}): boolean {
  return isOverheadLine(budgetLine) || isRabattLine(budgetLine);
}

/**
 * VIB-545: Skill oppgaver (intern timebasert) fra kostnader (fastpris).
 * Brukes både i Timetildeling-tab og i godkjenn-flyten så de viser samme
 * sett oppgaver — ellers kan en assignment "forsvinne" fra én av dem.
 */
export function isTaskLine(budgetLine: {
  cost_type?: string | null;
  quantity_hours?: number | null;
}): boolean {
  return budgetLine.cost_type === "internal" && (budgetLine.quantity_hours ?? 0) > 0;
}

/**
 * VIB-665: Upsell-metadata for BF-beregning. Per-upsell overhead/rabatt
 * legges på toppen av linjesummen, samme modell som hovedtilbudet. Drafts
 * og soft-deleted upsells filtreres ut før beregning så de IKKE teller i BF.
 */
export interface UpsellMeta {
  id: string;
  status?: string | null;
  overhead_pct?: number | null;
  discount_pct?: number | null;
  discount_amount?: number | null;
}

/**
 * Calculate live BF estimated from budget lines and offer metadata.
 *
 * BF-konsept: Bruttofortjeneste = Tilbudssum − eksterne estimerte kostnader.
 * Interne kostnader på oppgaver (timer × intern timekost) inngår IKKE i
 * BF-trekk — interne timer dekkes allerede av timeprisene som er fakturert.
 *
 * VIB-665: Mersalg har nå egen overhead/rabatt og godkjenningsflyt. Kun
 * `status='approved'`-upsells teller i BF; drafts og deleted ignoreres.
 * `upsells`-parameteret er valgfritt — uten det antas alle upsell-linjer
 * å være "approved" uten overhead/rabatt (bakoverkompatibilitet med
 * komponenter som ikke er oppdatert ennå).
 *
 * Returns an object with:
 * - regularTotal: Sum of non-overhead/non-rabatt/non-upsell line amounts
 * - overheadAmt: Calculated overhead amount (from offerMetadata or budget lines)
 * - discountAmt: Calculated discount amount (from offerMetadata or budget lines)
 * - upsellTotal: Sum of approved mersalg INKLUSIVE per-upsell overhead/rabatt
 * - upsellExtCostTotal: External estimated cost on approved mersalg-lines (BF-trekk)
 * - liveTotalAmount: regularTotal + overheadAmt - discountAmt + upsellTotal
 *   (+ travelTotal når include_travel_in_overhead er false)
 *   (reise inngår i regularTotal når flagget er true, VIB-2086)
 * - estExtCostTotal: Sum of estimated costs from EXTERNAL regular + upsell lines
 * - liveBfEstimated: liveTotalAmount - estExtCostTotal
 * - liveBfEstimatedPct: BF as percentage of liveTotalAmount
 * - regLines: Filtered regular (non-summary, non-upsell) budget lines
 * - upsellLines: Filtered mersalg-budget-lines (kun fra approved upsells hvis upsells er gitt)
 */
export function calcLiveBfEstimated(
  budgetLines: Array<{
    id?: string;
    description?: string | null;
    amount?: number | null;
    estimated_cost?: number | null;
    cost_type?: string | null;
    upsell_id?: string | null;
    phase?: string | null;
  }>,
  offerMetadata: {
    overhead_pct?: number;
    discount_pct?: number;
    discount_amount?: number;
    /**
     * VIB-2086: reise i overhead-grunnlaget. Mangler/undefined = false
     * (eksisterende prosjekt uten felt = ikke tilbakevirkende).
     */
    include_travel_in_overhead?: boolean;
  } | null,
  upsells?: UpsellMeta[],
) {
  // Skill ut mersalg-linjer; de skal IKKE være med i overhead/rabatt-
  // grunnlaget (egne per-upsell-satser). Reise INNGÅR i grunnlaget kun når
  // include_travel_in_overhead er true (VIB-2086). Mangler felt = gammel
  // formel (ikke tilbakevirkende).
  //
  // VIB-665: Hvis upsells-metadata er gitt, filtrer ut linjer som
  // tilhører drafts eller deleted upsells. Uten metadata regnes alle
  // upsell-linjer som approved (bakoverkompat).
  const includeTravelInOh = offerMetadata?.include_travel_in_overhead === true;
  const approvedUpsellIds = upsells
    ? new Set(upsells.filter((u) => (u.status ?? "approved") === "approved").map((u) => u.id))
    : null;
  const allUpsellLines = budgetLines.filter(isUpsellLine);
  const upsellLines = approvedUpsellIds
    ? allUpsellLines.filter((l) => l.upsell_id && approvedUpsellIds.has(l.upsell_id))
    : allUpsellLines;
  const travelLines = budgetLines.filter((l) => !isUpsellLine(l) && isTravelLine(l));
  const baseLines = budgetLines.filter((l) => !isUpsellLine(l));

  // Regulære linjer = non-upsell og non-summary. Reise kun når flagget er true.
  const regLines = baseLines.filter(
    (l) => !isSummaryLine(l) && (includeTravelInOh || !isTravelLine(l)),
  );

  // Calculate regular total (basis for overhead/discount)
  const regularTotal = regLines.reduce((s, l) => s + (l.amount ?? 0), 0);

  // Calculate overhead: prefer offerMetadata, fallback til overhead-rader.
  // VIKTIG: kun baseLines (uten mersalg) vurderes. Reise er med kun når flagget er true.
  const overheadFromLines = baseLines
    .filter(isOverheadLine)
    .reduce((s, l) => s + (l.amount ?? 0), 0);
  const overheadAmt = offerMetadata && offerMetadata.overhead_pct && offerMetadata.overhead_pct > 0
    ? regularTotal * offerMetadata.overhead_pct / 100
    : overheadFromLines;

  // Calculate discount: discount_amount (kr) overstyrer discount_pct (%)
  // hvis begge er satt. Faller tilbake til budget-linje-rabatt hvis ingen
  // metadata. Mersalg er ikke med i grunnlaget; reise kun når flagget er true.
  const rabattFromLines = Math.abs(
    baseLines
      .filter(isRabattLine)
      .reduce((s, l) => s + (l.amount ?? 0), 0)
  );
  const discountAmt = (() => {
    if (!offerMetadata) return rabattFromLines;
    if (offerMetadata.discount_amount && offerMetadata.discount_amount > 0) {
      return offerMetadata.discount_amount;
    }
    if (offerMetadata.discount_pct && offerMetadata.discount_pct > 0) {
      return (regularTotal + overheadAmt) * offerMetadata.discount_pct / 100;
    }
    return rabattFromLines;
  })();

  // VIB-665: Mersalg-summen inkluderer per-upsell overhead + rabatt
  // hvis upsells-metadata er gitt. Uten metadata oppfører det seg som før
  // (ren sum av linjer). overhead_pct=null → bruk prosjektets default.
  const defaultUpsellOverheadPct = offerMetadata?.overhead_pct ?? 0;
  const upsellLinesSum = upsellLines.reduce((s, l) => s + (l.amount ?? 0), 0);

  let upsellTotal = upsellLinesSum;
  if (upsells && upsells.length > 0) {
    // Re-beregn per upsell så hver får sin egen overhead/rabatt
    const linesByUpsell = new Map<string, number>();
    for (const l of upsellLines) {
      if (!l.upsell_id) continue;
      const prev = linesByUpsell.get(l.upsell_id) ?? 0;
      linesByUpsell.set(l.upsell_id, prev + (l.amount ?? 0));
    }
    upsellTotal = 0;
    for (const u of upsells) {
      if ((u.status ?? "approved") !== "approved") continue;
      const lineSum = linesByUpsell.get(u.id) ?? 0;
      const overheadPct = u.overhead_pct ?? defaultUpsellOverheadPct;
      const overheadAmount = (lineSum * overheadPct) / 100;
      const baseWithOverhead = lineSum + overheadAmount;
      let discount = 0;
      if (u.discount_amount && u.discount_amount > 0) {
        discount = u.discount_amount;
      } else if (u.discount_pct && u.discount_pct > 0) {
        discount = (baseWithOverhead * u.discount_pct) / 100;
      }
      upsellTotal += baseWithOverhead - discount;
    }
  }
  const travelTotal = travelLines.reduce((s, l) => s + (l.amount ?? 0), 0);

  // Eksterne kostnader: regulære + mersalg. Reise-ext trekkes separat kun
  // når reise ikke allerede ligger i regLines (ellers dobbeltelling).
  const regExtCostTotal = regLines
    .filter((l) => l.cost_type === "external")
    .reduce((s, l) => s + (l.estimated_cost ?? 0), 0);
  const upsellExtCostTotal = upsellLines
    .filter((l) => l.cost_type === "external")
    .reduce((s, l) => s + (l.estimated_cost ?? 0), 0);
  const travelExtCostTotal = travelLines
    .filter((l) => l.cost_type === "external")
    .reduce((s, l) => s + (l.estimated_cost ?? 0), 0);
  const estExtCostTotal =
    regExtCostTotal +
    upsellExtCostTotal +
    (includeTravelInOh ? 0 : travelExtCostTotal);

  const liveTotalAmount =
    regularTotal +
    overheadAmt -
    discountAmt +
    upsellTotal +
    (includeTravelInOh ? 0 : travelTotal);
  const liveBfEstimated = liveTotalAmount - estExtCostTotal;
  const liveBfEstimatedPct = liveTotalAmount > 0
    ? Math.round(liveBfEstimated / liveTotalAmount * 100)
    : 0;

  return {
    regularTotal,
    overheadAmt,
    discountAmt,
    upsellTotal,
    upsellExtCostTotal,
    travelTotal,
    travelExtCostTotal,
    liveTotalAmount,
    estExtCostTotal,
    liveBfEstimated,
    liveBfEstimatedPct,
    regLines,
    upsellLines,
    travelLines,
  };
}

// ── VIB-582: Provisjonssplitt med blanding av prosent + fastbeløp ─────────

export interface CommissionSplitInput {
  user_id: string;
  split_type: "percent" | "fixed";
  ownership_pct: number | null;
  fixed_amount: number | null;
}

export interface CommissionSplitResult {
  user_id: string;
  /** Andel av total BF (kr) for denne eieren */
  bf_share: number;
  /** Provisjonen (rate × bf_share) i kr */
  provision: number;
  /** Effektiv prosent av total BF som denne eieren får */
  effective_pct: number;
  split_type: "percent" | "fixed";
}

/**
 * Fordeler total BF mellom eierne. 'fixed'-rader får sitt faste kr-beløp først.
 * Resterende BF (BF − sum av fixed) fordeles prosentvis blant 'percent'-rader.
 *
 * Regler:
 *   - Hvis sum av fixed > total BF, kappes hver fixed ned til sin andel av BF
 *     (proporsjonal nedjustering) slik at vi aldri overdeler.
 *   - 'percent'-rader summeres ikke nødvendigvis til 100; vi normaliserer
 *     ved å bruke pct/sumOfPct slik at de fordeler resterende beløp helt opp.
 *   - Hvis det ikke finnes 'percent'-rader, beholdes resterende BF urørt
 *     (det er ikke vår jobb å gjette hvor det skal).
 */
export function calcCommissionSplits(
  totalBf: number,
  owners: CommissionSplitInput[],
  rate: number,
): CommissionSplitResult[] {
  if (owners.length === 0) return [];
  const safeBf = Math.max(0, Number(totalBf) || 0);

  const fixedOwners = owners.filter((o) => o.split_type === "fixed");
  const percentOwners = owners.filter((o) => o.split_type !== "fixed");

  let fixedTotal = fixedOwners.reduce((s, o) => s + (o.fixed_amount ?? 0), 0);
  const overshoot = fixedTotal > safeBf;
  const fixedScale = overshoot && fixedTotal > 0 ? safeBf / fixedTotal : 1;
  if (overshoot) fixedTotal = safeBf;

  const remainingBf = Math.max(0, safeBf - fixedTotal);
  const sumPct = percentOwners.reduce((s, o) => s + (o.ownership_pct ?? 0), 0);

  const out: CommissionSplitResult[] = [];

  for (const o of fixedOwners) {
    const bfShare = (o.fixed_amount ?? 0) * fixedScale;
    out.push({
      user_id: o.user_id,
      bf_share: bfShare,
      provision: bfShare * rate,
      effective_pct: safeBf > 0 ? (bfShare / safeBf) * 100 : 0,
      split_type: "fixed",
    });
  }

  for (const o of percentOwners) {
    const pct = o.ownership_pct ?? 0;
    const normalizedPct = sumPct > 0 ? pct / sumPct : 0;
    const bfShare = remainingBf * normalizedPct;
    out.push({
      user_id: o.user_id,
      bf_share: bfShare,
      provision: bfShare * rate,
      effective_pct: safeBf > 0 ? (bfShare / safeBf) * 100 : 0,
      split_type: "percent",
    });
  }

  return out;
}
